import { faCheck, faPen } from '@fortawesome/pro-regular-svg-icons';
import { isActionsColumn } from './columnLockable';
import type { ColumnConfig, RowConfig } from '@/shared/types/table';

/**
 * Añade columna de acciones de edición o fusiona con columna `actions` existente.
 * Misma semántica que el `useMemo` previo en `Table.tsx`.
 */
export function buildColumnsWithEditActions<T>(params: {
  effectiveColumns: ColumnConfig<T>[];
  hasEditableColumns: boolean;
  showEditActions: boolean | undefined;
}): ColumnConfig<T>[] {
  const { effectiveColumns, hasEditableColumns, showEditActions } = params;

  if (showEditActions === false) {
    return effectiveColumns;
  }

  if (!hasEditableColumns) {
    return effectiveColumns;
  }

  const existingActionsColumnIndex = effectiveColumns.findIndex(
    (col) => col.type === 'actions' && col.id !== '_editActions'
  );

  if (existingActionsColumnIndex !== -1) {
    const existingColumn = effectiveColumns[existingActionsColumnIndex];
    const originalRender = existingColumn.render;

    const modifiedColumn = {
      ...existingColumn,
      render: (value: unknown, row: RowConfig<T>, column: ColumnConfig<T>) => {
        const originalResult = originalRender ? originalRender(value, row, column) : [];
        const originalActions = Array.isArray(originalResult) ? (originalResult as unknown[]) : [];

        const editActionWithState = {
          icon: row._isEditing ? faCheck : faPen,
          tooltip: row._isEditing ? 'Guardar' : 'Editar',
          color: row._isEditing ? 'secondary' : 'tertiary',
          onClick: () => {
            if (row._toggleEdit) {
              row._toggleEdit();
            }
          },
        };

        return [editActionWithState, ...originalActions];
      },
    } as (typeof effectiveColumns)[0];

    const newColumns = [...effectiveColumns];
    newColumns[existingActionsColumnIndex] = modifiedColumn as ColumnConfig<T>;
    return newColumns;
  }

  const editActionsColumn = {
    id: '_editActions',
    header: 'Acciones',
    type: 'actions' as const,
    sortable: false,
    filterable: false,
    lockable: false,
    resizable: false,
    editable: 'none' as const,
    render: (_: unknown, row: RowConfig<T>) => [
      {
        icon: row._isEditing ? faCheck : faPen,
        tooltip: row._isEditing ? 'Guardar ' : 'Editar',
        onClick: () => {
          if (row._toggleEdit) {
            row._toggleEdit();
          }
        },
      },
    ],
  } as (typeof effectiveColumns)[0];

  return [...effectiveColumns, editActionsColumn as ColumnConfig<T>];
}

/**
 * Marca `resizable: true` en columnas sin `width` explícito (salvo `resizable === false`).
 */
export function applyAutoResizeFlagsToColumns<T>(columns: ColumnConfig<T>[]): ColumnConfig<T>[] {
  return columns.map((col) => {
    if (isActionsColumn(col)) {
      return { ...col, resizable: false };
    }
    if (col.width === undefined && col.resizable !== false) {
      return { ...col, resizable: true };
    }
    return col;
  });
}
